interface ChessPuzzle {
    title: string;
    fen: string;
    pgn: string;
    moves: string[];
    playerColor: "w" | "b";
}

/**
 * Fetches the daily puzzle from Chess.com API
 * @returns Promise with puzzle data or null if fetch fails
 */
export async function fetchDailyPuzzle(): Promise<ChessPuzzle | null> {
    try {
        const response = await fetch("https://api.chess.com/pub/puzzle");

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Extract solution moves from PGN
        const solutionMoves = extractMovesFromPGN(data.pgn);

        // Determine player color from FEN
        // FEN format: <piece placement> <activeColor> <castling> <enPassant> <halfmove> <fullmove>
        const fenParts = data.fen.split(" ");
        const activeColor = fenParts[1]; // 'w' or 'b' - who moves in this position

        const playerColor = activeColor as "w" | "b";

        return {
            title: data.title || "Daily Puzzle",
            fen: data.fen,
            pgn: data.pgn,
            moves: solutionMoves,
            playerColor,
        };
    } catch (error) {
        console.error("Error fetching daily puzzle:", error);
        return null;
    }
}

/**
 * Extracts moves from PGN string
 */
function extractMovesFromPGN(pgn: string): string[] {
    // remove headers, comments, variations and move numbers
    let cleanPgn = pgn.replace(/\[([^\]]+)\]/g, "");
    cleanPgn = cleanPgn.replace(/\{[^}]*\}/g, "");
    cleanPgn = cleanPgn.replace(/\([^)]*\)/g, "");
    cleanPgn = cleanPgn.replace(/\d+\.\s*/g, ""); // Remove move numbers like "1. "
    cleanPgn = cleanPgn.replace(/\s*(1-0|0-1|1\/2-1\/2|\*)\s*$/g, "");
    cleanPgn = cleanPgn.replace(/\s+/g, " ").trim();

    return cleanPgn
        .split(" ")
        .filter((move) => move.length > 0 && move !== "*" && move !== ".." && move !== "...")
        .map((move) => move.replace(/^\.+/, "")); // Remove leading dots from moves like "..Qb1+"
}

/**
 * Caches puzzle data in memory with timestamp
 */
let cachedPuzzle: { puzzle: ChessPuzzle; timestamp: number } | null = null;
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour cache

export async function getDailyPuzzle(): Promise<ChessPuzzle | null> {
    const now = Date.now();

    if (cachedPuzzle && now - cachedPuzzle.timestamp < CACHE_DURATION) {
        return cachedPuzzle.puzzle;
    }

    const puzzle = await fetchDailyPuzzle();

    if (puzzle) cachedPuzzle = { puzzle, timestamp: now };

    return puzzle;
}

/**
 * Fetches a random puzzle from Chess.com API
 * @returns Promise with puzzle data or null if fetch fails
 */
export async function fetchRandomPuzzle(): Promise<ChessPuzzle | null> {
    try {
        const response = await fetch("https://api.chess.com/pub/puzzle/random");

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Extract solution moves from PGN
        const solutionMoves = extractMovesFromPGN(data.pgn);

        // Determine player color from FEN
        const fenParts = data.fen.split(" ");
        const activeColor = fenParts[1];
        const playerColor = activeColor as "w" | "b";

        return {
            title: data.title || "Chess Puzzle",
            fen: data.fen,
            pgn: data.pgn,
            moves: solutionMoves,
            playerColor,
        };
    } catch (error) {
        console.error("Error fetching random puzzle:", error);
        return null;
    }
}

/**
 * Fallback puzzle in case API fails
 * Player should be white in this puzzle (FEN has 'w' to move)
 */
export const FALLBACK_PUZZLE: ChessPuzzle = {
    title: "Practice Puzzle",
    fen: "r1bqkbnr/pppp1ppp/2n5/4p3/1b2P3/2NP1N2/PPP2PPP/R1BQKB1R w KQkq - 0 1",
    pgn: "1. Nxe5 Nxe5 2. d4 Bxc3+ 3. bxc3",
    moves: ["Nxe5", "Nxe5", "d4", "Bxc3+", "bxc3"],
    playerColor: "w", // MATCHES FEN: white moves first
};
